import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Beaker,
  FolderOpen,
  Search,
  ShieldAlert,
  Sparkles,
  Trophy,
} from "lucide-react";
import { useProblemRecords } from "@/lib/problem-record-store";
import { currency } from "@/lib/top5-data";
import {
  MANAGEMENT_STATUSES,
  type ManagementStatus,
  type ProblemRecord,
} from "@/lib/problem-record-data";
import { currentStageLabel } from "@/lib/problem-record-workflow";
import { LiveProblemRecordRegistry } from "@/components/LiveProblemRecords";
import { usePortalRuntime } from "@/lib/portal-runtime";

export const Route = createFileRoute("/problem-records")({
  head: () => ({
    meta: [
      { title: "Problem Record Registry — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Operational portfolio of closed-loop Problem Records: severity, exposure, current stage, owner and attention flags across the warranty quality queue.",
      },
      {
        property: "og:title",
        content: "Problem Record Registry — Broadview Warranty Intelligence",
      },
      {
        property: "og:description",
        content: "Quality leadership work queue for every open and closed Problem Record.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PortalRegistryPage,
});

const severityTone: Record<string, string> = {
  Critical: "border-destructive/40 bg-destructive/10 text-destructive",
  High: "border-warning/40 bg-warning/10 text-warning",
  Medium: "border-primary/40 bg-primary/10 text-primary",
  Low: "border-border bg-muted text-muted-foreground",
};

const mgmtOf = (r: ProblemRecord): ManagementStatus => r.managementStatus ?? "Active";
const isClosed = (r: ProblemRecord) => mgmtOf(r) === "Closed";
const currentStage = (r: ProblemRecord) => currentStageLabel(r);

const mgmtTone: Record<ManagementStatus, string> = {
  Draft: "border-border bg-muted text-muted-foreground",
  Active: "border-primary/50 bg-primary/15 text-primary",
  "On Hold": "border-warning/40 bg-warning/10 text-warning",
  Escalated: "border-destructive/40 bg-destructive/10 text-destructive",
  Closed: "border-success/40 bg-success/10 text-success",
};

function attentionFor(r: ProblemRecord): string | null {
  if (isClosed(r)) return null;
  if (r.attention) return r.attention;
  if (r.daysOpen > 150) return `Aging — ${r.daysOpen} days open`;
  if (r.severity === "Critical" && r.riskScore >= 85) return "Critical risk — executive review";
  if (r.isDraft) return "Draft — awaiting problem definition";
  return null;
}

function PortalRegistryPage() {
  const { mode } = usePortalRuntime();
  return mode === "live" ? <LiveProblemRecordRegistry /> : <RegistryPrototypePage />;
}

function RegistryPrototypePage() {
  const records = useProblemRecords();
  const [status, setStatus] = useState<string>("Open");
  const [severity, setSeverity] = useState("All");
  const [stage, setStage] = useState("All");
  const [owner, setOwner] = useState("All");
  const [source, setSource] = useState("All");
  const [q, setQ] = useState("");

  const stages = useMemo(() => Array.from(new Set(records.map(currentStage))).sort(), [records]);
  const owners = useMemo(() => Array.from(new Set(records.map((r) => r.owner))).sort(), [records]);
  const sources = useMemo(
    () => Array.from(new Set(records.map((r) => r.source.split(" · ")[0]))).sort(),
    [records],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return records
      .filter((r) =>
        status === "All" ? true : status === "Open" ? !isClosed(r) : mgmtOf(r) === status,
      )
      .filter((r) => severity === "All" || r.severity === severity)
      .filter((r) => stage === "All" || currentStage(r) === stage)
      .filter((r) => owner === "All" || r.owner === owner)
      .filter((r) => source === "All" || r.source.startsWith(source))
      .filter((r) =>
        !term
          ? true
          : [r.id, r.title, r.component, r.supplier, ...r.products, ...r.carLines]
              .join(" ")
              .toLowerCase()
              .includes(term),
      )
      .slice()
      .sort((a, b) => {
        const ac = isClosed(a) ? 1 : 0;
        const bc = isClosed(b) ? 1 : 0;
        if (ac !== bc) return ac - bc;
        if (b.riskScore !== a.riskScore) return b.riskScore - a.riskScore;
        return b.warrantyCost - a.warrantyCost;
      });
  }, [records, status, severity, stage, owner, source, q]);

  const open = records.filter((r) => !isClosed(r));
  const kpis = [
    {
      l: "Open Problem Records",
      v: String(open.length),
      sub: `${records.length} total in registry`,
    },
    {
      l: "Critical / High open",
      v: String(open.filter((r) => r.severity === "Critical" || r.severity === "High").length),
      sub: "Executive escalation band",
    },
    {
      l: "Active warranty exposure",
      v: currency(open.reduce((s, r) => s + r.warrantyCost, 0)),
      sub: "Sum of open record exposure",
    },
    {
      l: "In Validation / Effectiveness",
      v: String(
        open.filter((r) => ["Validation", "Effectiveness"].includes(currentStage(r))).length,
      ),
      sub: "Closing the loop",
    },
    {
      l: "Attention required",
      v: String(open.filter((r) => attentionFor(r)).length),
      sub: "Overdue, stalled or draft",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <FolderOpen className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Problem Record Registry</div>
              <div className="text-xs text-muted-foreground">
                Closed-loop quality portfolio · Broadview
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            <Link
              to="/top5"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
            >
              <Trophy className="h-3.5 w-3.5" /> Top 5 Intelligence
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">Problem Record Registry</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Operational work queue for quality, warranty and engineering leadership. Highest-risk,
            highest-exposure active records surface first.
          </p>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {kpis.map((k) => (
            <div key={k.l} className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">{k.l}</div>
              <div className="mt-1 text-2xl font-semibold">{k.v}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">{k.sub}</div>
            </div>
          ))}
        </section>

        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search PR ID, title, product, component, supplier…"
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-xs outline-none focus:border-primary"
              />
            </div>
            <Select
              label="Management"
              value={status}
              onChange={setStatus}
              options={["Open", "All", ...MANAGEMENT_STATUSES]}
            />
            <Select
              label="Severity"
              value={severity}
              onChange={setSeverity}
              options={["All", "Critical", "High", "Medium", "Low"]}
            />
            <Select label="Stage" value={stage} onChange={setStage} options={["All", ...stages]} />
            <Select label="Owner" value={owner} onChange={setOwner} options={["All", ...owners]} />
            <Select
              label="Source"
              value={source}
              onChange={setSource}
              options={["All", ...sources]}
            />
            <span className="ml-auto text-xs text-muted-foreground">{filtered.length} records</span>
          </div>
        </section>

        <section className="space-y-3">
          {filtered.map((r) => {
            const attention = attentionFor(r);
            return (
              <Link
                key={r.id}
                to="/problem-record/$id"
                params={{ id: r.id }}
                className="block rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/50 hover:bg-muted/40"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-primary/15 px-2 py-0.5 font-semibold text-primary">
                    {r.id}
                  </span>
                  <span
                    className={`rounded border px-2 py-0.5 font-medium ${severityTone[r.severity]}`}
                  >
                    {r.severity} · Risk {r.riskScore}
                  </span>
                  <span className={`rounded border px-2 py-0.5 font-medium ${mgmtTone[mgmtOf(r)]}`}>
                    {mgmtOf(r)}
                  </span>
                  <span className="rounded border border-border px-2 py-0.5 text-muted-foreground">
                    Stage: {currentStage(r)}
                  </span>

                  {r.origin === "created" && (
                    <span className="inline-flex items-center gap-1 rounded border border-primary/50 bg-primary/15 px-2 py-0.5 font-medium text-primary">
                      <Sparkles className="h-3 w-3" /> Newly created
                    </span>
                  )}
                  {attention && (
                    <span className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-2 py-0.5 font-medium text-warning">
                      <AlertTriangle className="h-3 w-3" /> {attention}
                    </span>
                  )}
                  <span className="ml-auto text-muted-foreground">{r.source}</span>
                </div>

                <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{r.title}</div>
                    <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded bg-muted px-2 py-0.5">{r.products.join(", ")}</span>
                      <span className="rounded bg-muted px-2 py-0.5">{r.carLines.join(", ")}</span>
                      <span className="rounded bg-muted px-2 py-0.5">{r.component}</span>
                      <span className="rounded bg-muted px-2 py-0.5">{r.supplier}</span>
                      <span className="rounded bg-muted px-2 py-0.5">Owner: {r.owner}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-5 lg:max-w-2xl">
                    <Cell l="Claims" v={r.totalClaims.toLocaleString()} />
                    <Cell l="Exposure" v={currency(r.warrantyCost)} />
                    <Cell l="C / 1,000" v={r.claimsPer1000 ? r.claimsPer1000.toFixed(1) : "—"} />
                    <Cell
                      l="Current stage"
                      v={currentStage(r)}
                      icon={currentStage(r) === "Validation" ? Beaker : undefined}
                    />
                    <Cell l="Aging" v={isClosed(r) ? "Closed" : `${r.daysOpen} days`} />
                  </div>
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
              <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              No Problem Records match these filters.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Cell({ l, v, icon: Icon }: { l: string; v: string; icon?: typeof Beaker }) {
  return (
    <div>
      <div className="text-muted-foreground">{l}</div>
      <div className="inline-flex items-center gap-1 text-sm font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
        {v}
      </div>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none focus:border-primary"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
